// src/components/support/AdminSupportPage.jsx
//
// Internal "Support" page (/admin/support): every client ticket, urgent and
// new first. The stat cards double as filters. ?ticket=<id> opens one
// directly - the push notification and email for a new ticket link there.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../../api/apiClient';
import FleetHeader from '../FleetHeader';
import { LoadingScreen } from '../billing/billingShared';
import TicketDrawer from './TicketDrawer';
import { PriorityTag, TicketStatus, timeAgo } from './supportShared';
import '../../styles/fleet-theme.css';
import '../billing/Billing.css';
import './Support.css';

const STATUS_FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'new', label: 'New' },
  { id: 'waiting_client', label: 'Waiting on client' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'closed', label: 'Closed' },
  { id: 'all', label: 'All' },
];

export default function AdminSupportPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState(null);
  const [summary, setSummary] = useState(null);
  const [staff, setStaff] = useState([]);
  const [me, setMe] = useState(user?.username);
  const [status, setStatus] = useState('open');
  const [assigned, setAssigned] = useState('');
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [q, setQ] = useState('');
  const [error, setError] = useState(null);

  const openId = Number(new URLSearchParams(location.search).get('ticket')) || null;
  const openTicket = (id) => navigate(id ? `/admin/support?ticket=${id}` : '/admin/support', { replace: !id });

  const load = useCallback(async () => {
    const params = new URLSearchParams({ status });
    if (assigned) params.set('assigned', assigned);
    if (q.trim()) params.set('q', q.trim());
    try {
      const [list, sum] = await Promise.all([
        apiFetchJSON(`/api/v2/admin/support/tickets?${params}`),
        apiFetchJSON('/api/v2/admin/support/summary'),
      ]);
      setTickets(list.tickets);
      setSummary(sum);
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not load tickets');
      setTickets((t) => t || []);
    }
  }, [status, assigned, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0); // debounce typing in search
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => {
    const id = setInterval(() => document.visibilityState === 'visible' && load(), 30000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    apiFetchJSON('/api/v2/admin/support/staff')
      .then((d) => { setStaff(d.staff); setMe(d.me); })
      .catch(() => {});
  }, []);

  const rows = useMemo(
    () => (tickets || []).filter((t) => !urgentOnly || t.priority === 'urgent'),
    [tickets, urgentOnly]
  );

  const pickCard = (next) => {
    setUrgentOnly(!!next.urgent);
    setStatus(next.status);
    setAssigned(next.assigned || '');
  };
  const cardActive = (c) => status === c.status && (assigned || '') === (c.assigned || '') && urgentOnly === !!c.urgent;

  if (!tickets) return <LoadingScreen text="Loading support…" />;

  const cards = [
    { key: 'new', label: 'New, unassigned', value: summary?.new, status: 'new', attention: summary?.new > 0 },
    { key: 'urgent', label: 'Urgent open', value: summary?.urgent, status: 'open', urgent: true, attention: summary?.urgent > 0 },
    { key: 'mine', label: 'Assigned to me', value: summary?.mine, status: 'open', assigned: 'me' },
    { key: 'waiting', label: 'Waiting on client', value: summary?.waiting, status: 'waiting_client' },
  ];

  return (
    <div>
      <FleetHeader title="Support" user={user} onLogout={onLogout} scope="admin" />
      <div className="sd-page bl-page bl-page-admin sp-page">
        {error && <div className="ft-toolbar-error" role="alert">{error}</div>}

        <div className="ft-stat-row bl-stats sp-stats">
          {cards.map((c) => (
            <button key={c.key} type="button" onClick={() => pickCard(c)} aria-pressed={cardActive(c)}
                    className={`ft-stat-card bl-stat-btn ${c.attention ? 'ft-alert' : ''} ${cardActive(c) ? 'ft-stat-active' : ''}`}>
              <div className="ft-stat-label">{c.label}</div>
              <div className={`ft-stat-value ${c.attention ? 'ft-value-accent' : ''}`}>{c.value ?? '—'}</div>
            </button>
          ))}
        </div>

        <div className="ft-filter-row bl-filters sp-admin-filters">
          <div className="bl-filter-group bl-status-pills">
            {STATUS_FILTERS.map((f) => (
              <button key={f.id} type="button" className={'ft-pill' + (status === f.id && !urgentOnly ? ' active' : '')}
                      onClick={() => { setStatus(f.id); setUrgentOnly(false); }}>
                {f.label}
              </button>
            ))}
          </div>
          <select className="bl-select bl-status-select" value={status} onChange={(e) => { setStatus(e.target.value); setUrgentOnly(false); }} aria-label="Status">
            {STATUS_FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <div className="bl-filter-group sp-filter-right">
            <select className="bl-select" value={assigned} onChange={(e) => setAssigned(e.target.value)} aria-label="Assignee">
              <option value="">Anyone</option>
              <option value="me">Assigned to me</option>
              <option value="none">Unassigned</option>
            </select>
            <input type="search" className="bl-select sp-search" placeholder="Search ticket, client, vessel"
                   value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search tickets" />
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="bl-empty">
            {q ? 'No tickets match that search.' : status === 'open' ? 'No open tickets. Nothing waiting on the team.' : 'No tickets here.'}
          </div>
        ) : (
          <>
            <div className="bl-table-wrap bl-table-wrap-admin">
              <table className="ft-table bl-table sp-table">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Client / vessel</th>
                    <th>Status</th>
                    <th>Assignee</th>
                    <th>Opened</th>
                    <th>Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className={`bl-row ${t.unread ? 'is-unread' : ''} ${t.priority === 'urgent' && t.is_open ? 'is-urgent' : ''}`}
                        onClick={() => openTicket(t.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && openTicket(t.id)}>
                      <td>
                        <div className="sp-cell-subject">
                          {t.unread && <span className="sp-unread-dot" aria-label="New activity" />}
                          <span className="bl-clamp">{t.subject}</span>
                        </div>
                        <div className="bl-small bl-muted"><span className="ft-mono">{t.ref}</span> · {t.category_label}</div>
                      </td>
                      <td>
                        {t.client_name || '—'}
                        <div className="bl-small bl-muted">{t.site_name || 'Whole account'}</div>
                      </td>
                      <td><span className="sp-row-tags"><PriorityTag priority={t.priority} /><TicketStatus status={t.status} scope="admin" /></span></td>
                      <td className={t.assigned_to ? '' : 'bl-muted'}>{t.assigned_to_name || 'Unassigned'}</td>
                      <td className="ft-mono bl-small">{timeAgo(t.created_at)}</td>
                      <td className="ft-mono bl-small">{timeAgo(t.last_activity_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sp-acards">
              {rows.map((t) => (
                <button key={t.id} type="button" onClick={() => openTicket(t.id)}
                        className={`bl-acard sp-acard ${t.priority === 'urgent' && t.is_open ? 'is-overdue' : ''} ${t.unread ? 'is-unread' : ''}`}>
                  <div className="bl-acard-row">
                    <span className="bl-acard-id">
                      {t.unread && <span className="sp-unread-dot" aria-label="New activity" />}
                      <span className="ft-mono">{t.ref}</span>
                    </span>
                    <span className="sp-row-tags"><PriorityTag priority={t.priority} /><TicketStatus status={t.status} scope="admin" /></span>
                  </div>
                  <div className="bl-acard-who sp-acard-subject">{t.subject}</div>
                  <div className="bl-acard-row bl-small bl-muted">
                    <span>{t.client_name}{t.site_name ? ` · ${t.site_name}` : ''}</span>
                    <span>{t.assigned_to_name || 'Unassigned'} · {timeAgo(t.last_activity_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {openId && (
        <TicketDrawer
          scope="admin"
          ticketId={openId}
          staff={staff}
          me={me}
          onClose={() => { openTicket(null); load(); }}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}
